/**
 * SavedRepliesPicker — searchable dropdown for inserting saved reply bodies.
 * Props:
 *   replies    {Array}                        — pre-loaded replies array (optional; fetches if not provided)
 *   onSelect   {function(replyBody: string)} — called when user selects a reply
 *   onClose    {function}                    — called when picker should close
 */

import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'

export function SavedRepliesPicker({ replies: repliesProp, onSelect, onClose }) {
  const [replies,  setReplies]  = useState(repliesProp || [])
  const [loading,  setLoading]  = useState(!repliesProp)
  const [search,   setSearch]   = useState('')
  const [error,    setError]    = useState(null)

  useEffect(() => {
    if (repliesProp) { setReplies(repliesProp); return }
    async function load() {
      try {
        const res = await fetch('/admin/support/saved_replies.json', {
          headers: { Accept: 'application/json' },
        })
        if (res.ok) {
          setReplies(await res.json())
        } else {
          setError('Nem sikerült betölteni az elmentett válaszokat.')
        }
      } catch (err) {
        setError('Hálózati hiba.')
        console.error('[SavedRepliesPicker] load error', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [repliesProp])

  const filtered = search
    ? replies.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        (r.shortcut && r.shortcut.toLowerCase().includes(search.toLowerCase()))
      )
    : replies

  function handleSelect(reply) {
    onSelect(reply.body_html || reply.body_text || '')
    onClose?.()
  }

  return (
    <div class="saved-replies-picker">
      <div class="saved-replies-header">
        <i class="bi bi-chat-quote me-1" />
        Elmentett válasz
        <button
          type="button"
          class="saved-replies-close-btn"
          aria-label="Bezárás"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div class="saved-replies-search">
        <input
          type="text"
          placeholder="Keresés..."
          value={search}
          onInput={e => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      <div class="saved-replies-list">
        {loading && (
          <div class="saved-replies-loading">
            <div class="spinner-border spinner-border-sm support-spinner-sm" role="status" />
            <span>Betöltés...</span>
          </div>
        )}

        {error && (
          <div class="saved-replies-empty error">{error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div class="saved-replies-empty">
            {search ? 'Nincs találat.' : 'Még nincsenek elmentett válaszok.'}
          </div>
        )}

        {!loading && filtered.map(reply => (
          <div
            key={reply.id}
            class="saved-reply-item"
            role="button"
            tabIndex={0}
            onClick={() => handleSelect(reply)}
            onKeyDown={e => e.key === 'Enter' && handleSelect(reply)}
          >
            <div class="saved-reply-title">{reply.name}</div>
            {reply.shortcut && (
              <code class="saved-reply-shortcut">
                /{reply.shortcut}
              </code>
            )}
            <div class="saved-reply-preview">
              {(reply.body_text || '').slice(0, 80)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
